import { cn } from "@/lib/utils";
import { PageHomeWhyIqCardV2Icon } from "@/sanity/pages/PageHomeTemplate";

/**
 * Thin-line icons for the Why IQ V2 bento cards. Path data is the exported
 * Figma vector layers (23.2639 unit box, 0.727 stroke), not redrawn.
 */
const ICON_PATHS: Record<PageHomeWhyIqCardV2Icon, string[]> = {
  temple: [
    "M2.90799 19.6289H20.3559",
    "M4.36133 19.6298V9.45183L11.6313 5.08984L18.9013 9.45183V19.6298",
    "M8.72461 19.628V14.5391H14.5406V19.628",
    "M11.6328 2.17969V5.08768",
  ],
  "chart-bar": [
    "M2.90799 19.6289H20.3559",
    "M7.26975 13.8125H3.63477V19.6285H7.26975V13.8125Z",
    "M13.0859 9.45096H9.45096V19.6289H13.0859V9.45096Z",
    "M18.9026 4.36328H15.2676V19.6302H18.9026V4.36328Z",
  ],
  document: [
    "M18.1743 2.9082H5.08833C4.68682 2.9082 4.36133 3.23369 4.36133 3.6352V19.6291C4.36133 20.0306 4.68682 20.3561 5.08833 20.3561H18.1743C18.5758 20.3561 18.9013 20.0306 18.9013 19.6291V3.6352C18.9013 3.23369 18.5758 2.9082 18.1743 2.9082Z",
    "M7.99609 8.72266H15.2661M7.99609 12.3576H15.2661M7.99609 15.9926H12.3581",
  ],
  scale: [
    "M11.6328 2.9082V20.3561",
    "M5.81641 7.26953H17.4484",
    "M5.81619 7.26953L2.9082 13.0855H8.72418L5.81619 7.26953Z",
    "M17.4471 7.26953L14.5391 13.0855H20.355L17.4471 7.26953Z",
    "M8.72461 20.3555H14.5406",
  ],
  percent: [
    "M11.6319 20.3559C16.4501 20.3559 20.3559 16.4501 20.3559 11.6319C20.3559 6.81384 16.4501 2.90799 11.6319 2.90799C6.81384 2.90799 2.90799 6.81384 2.90799 11.6319C2.90799 16.4501 6.81384 20.3559 11.6319 20.3559Z",
    "M15.9935 7.26953L7.26953 15.9935",
    "M8.72353 10.1775C9.52654 10.1775 10.1775 9.52654 10.1775 8.72353C10.1775 7.92051 9.52654 7.26953 8.72353 7.26953C7.92051 7.26953 7.26953 7.92051 7.26953 8.72353C7.26953 9.52654 7.92051 10.1775 8.72353 10.1775Z",
    "M14.5399 15.9939C15.343 15.9939 15.9939 15.343 15.9939 14.5399C15.9939 13.7369 15.343 13.0859 14.5399 13.0859C13.7369 13.0859 13.0859 13.7369 13.0859 14.5399C13.0859 15.343 13.7369 15.9939 14.5399 15.9939Z",
  ],
  dollar: [
    "M11.6328 0.078125V18.98",
    "M15.9935 6.54275C15.9935 4.94336 14.0306 3.63477 11.6315 3.63477C9.23242 3.63477 7.26953 4.94336 7.26953 6.54275C7.26953 8.14215 9.23242 9.45074 11.6315 9.45074C14.0306 9.45074 15.9935 10.7593 15.9935 12.3587C15.9935 13.9581 14.0306 15.2667 11.6315 15.2667C9.23242 15.2667 7.26953 13.9581 7.26953 12.3587",
  ],
  cash: [
    "M20.3566 5.81641H2.90864C2.50713 5.81641 2.18164 6.14189 2.18164 6.5434V16.7214C2.18164 17.1229 2.50713 17.4484 2.90864 17.4484H20.3566C20.7581 17.4484 21.0836 17.1229 21.0836 16.7214V6.5434C21.0836 6.14189 20.7581 5.81641 20.3566 5.81641Z",
    "M11.6326 14.5386C13.2386 14.5386 14.5406 13.2367 14.5406 11.6306C14.5406 10.0246 13.2386 8.72266 11.6326 8.72266C10.0266 8.72266 8.72461 10.0246 8.72461 11.6306C8.72461 13.2367 10.0266 14.5386 11.6326 14.5386Z",
    "M5.81641 8.72266V14.5386M17.4484 8.72266V14.5386",
  ],
  "chart-line": [
    "M2.90799 17.4479L7.99697 11.6319L12.3589 15.2669L15.9939 9.45096L20.3559 13.8129",
    "M2.9082 20.3555H20.3561",
    "M2.90799 2.90799V17.4479",
  ],
};

export function WhyIqIconV2({ icon, className }: { icon?: string; className?: string }) {
  const paths = ICON_PATHS[icon as PageHomeWhyIqCardV2Icon] ?? ICON_PATHS.document;

  return (
    <svg
      viewBox="0 0 23.2639 23.2639"
      fill="none"
      stroke="currentColor"
      strokeWidth={0.727}
      aria-hidden
      className={cn("h-[2.908rem] w-[2.908rem] shrink-0", className)}>
      {paths.map((d) => (
        <path
          key={d}
          d={d}
        />
      ))}
    </svg>
  );
}
